import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import {
  HiMenu,
  HiBell,
  HiUser,
  HiCog,
  HiLogout,
  HiGlobe,
} from 'react-icons/hi'
import { useAuth } from '@/hooks/useAuth'
import { LANGUAGES, ROUTES } from '@/utils/constants'
import { getInitials } from '@/utils/formatters'
import ThemeToggle from '@/components/UI/ThemeToggle'
import Breadcrumbs from '@/components/UI/Breadcrumbs'

const Header = ({ onMobileMenuToggle, isCollapsed }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)

  // Change language
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode)
    setIsLangMenuOpen(false)
  }

  // Handle logout
  const handleLogout = async () => {
    await logout()
    navigate(ROUTES.LOGIN)
  }

  // Get current language
  const currentLang = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0]

  return (
    <header
      className={classNames(
        'fixed top-0 right-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 transition-all duration-300',
        {
          'left-64': !isCollapsed,
          'left-20': isCollapsed,
          'left-0 lg:left-64': isCollapsed, // Mobile always full width
        }
      )}
    >
      {/* Top row - Actions */}
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Left side - Mobile menu button */}
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Open menu"
        >
          <HiMenu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
        </button>

        {/* Skip to main content (accessibility) */}
        <a
          href="#main-content"
          className="skip-link"
        >
          Aller au contenu principal
        </a>

        {/* Search bar (optional - can be expanded later) */}
        <div className="hidden md:flex flex-1 max-w-lg mx-4">
          {/* Search component can go here */}
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          {/* Dark mode toggle */}
          <ThemeToggle />

          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Change language"
              title={t('settings.language')}
            >
              <HiGlobe className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">
                {currentLang.flag}
              </span>
            </button>

            {/* Language dropdown */}
            {isLangMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsLangMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={classNames(
                        'flex items-center gap-3 w-full px-4 py-2 text-left text-sm hover:bg-gray-50',
                        {
                          'bg-primary-50 text-primary-600': i18n.language === lang.code,
                        }
                      )}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Notifications */}
          <button
            className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Notifications"
            title={t('settings.notifications')}
          >
            <HiBell className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            {/* Notification badge */}
            <span className="absolute top-1 right-1 w-2 h-2 bg-error-500 rounded-full" />
          </button>

          {/* User profile menu */}
          <div className="relative ml-2">
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="User menu"
            >
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  getInitials(user?.firstName + ' ' + user?.lastName)
                )}
              </div>
              <span className="hidden lg:block text-sm font-medium">
                {user?.firstName}
              </span>
            </button>

            {/* Profile dropdown */}
            {isProfileMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsProfileMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.prenom || user?.nom ? `${user?.prenom || ''} ${user?.nom || ''}`.trim() : user?.username}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        navigate(ROUTES.PROFILE)
                        setIsProfileMenuOpen(false)
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <HiUser className="w-5 h-5" />
                      {t('nav.profile')}
                    </button>
                    <button
                      onClick={() => {
                        navigate(ROUTES.SETTINGS)
                        setIsProfileMenuOpen(false)
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <HiCog className="w-5 h-5" />
                      {t('nav.settings')}
                    </button>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-200 py-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-error-600 hover:bg-gray-50"
                    >
                      <HiLogout className="w-5 h-5" />
                      {t('auth.logout')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom row - Breadcrumbs */}
      <div className="h-10 px-4 lg:px-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 hidden lg:flex items-center">
        <Breadcrumbs />
      </div>
    </header>
  )
}

export default Header


